import React, { useEffect, useState } from 'react';
import '../workorders/css/workorderModal.css';
import { FaTrash, FaEdit, FaFileAlt, FaShoppingBag, FaCheck } from 'react-icons/fa';
import Swal from 'sweetalert2';

const WO_STEPS = ['DRAFT', 'PENDING', 'APPROVED', 'COMPLETED'];
const STEP_LABEL = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  COMPLETED: 'Completed'
};
const STEP_DESC = {
  DRAFT: 'PO Draft Created',
  PENDING: 'Awaiting Sign-off',
  APPROVED: 'Printing & Production',
  COMPLETED: 'All Books Delivered'
};

function StatusTimeline({ status }) {
  const isCancelled = status === 'CANCELLED';
  const idx = WO_STEPS.indexOf(status);

  return (
    <div style={{ margin: '24px 0', width: '100%' }}>
      {/* Cancellation Ribbon */}
      {isCancelled && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.07)',
          border: '1px dashed rgba(239, 68, 68, 0.4)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '28px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          color: '#ef4444',
          boxShadow: '0 8px 32px 0 rgba(239, 68, 68, 0.05)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.5s ease-out',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            animation: 'pulseRed 2s infinite'
          }}>
            ⚠️
          </div>
          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Work Order Terminated</h5>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#f87171', lineHeight: '1.4' }}>
              This procurement contract has been officially **CANCELLED**. All operations, book printing processes, and supplier payouts are permanently suspended.
            </p>
          </div>
        </div>
      )}

      {/* Stepper Container */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        padding: '10px 0 20px 0',
        overflowX: 'auto',
        gap: '8px'
      }}>
        {WO_STEPS.map((s, i) => {
          const done = !isCancelled && i <= idx;
          const active = !isCancelled && i === idx;
          
          return (
            <React.Fragment key={s}>
              {/* Step circle & text info */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: '100px',
                textAlign: 'center',
                zIndex: 2,
                opacity: isCancelled ? 0.5 : 1,
                transition: 'opacity 0.3s ease'
              }}>
                {/* Step Circle */}
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: active 
                    ? 'rgba(16, 185, 129, 0.15)' 
                    : done 
                      ? 'rgba(16, 185, 129, 0.1)' 
                      : 'rgba(241, 245, 249, 0.6)',
                  border: active
                    ? '2px solid #10b981'
                    : done
                      ? '2px solid #059669'
                      : '2px solid #cbd5e1',
                  color: active || done ? '#059669' : '#94a3b8',
                  fontSize: '15px',
                  fontWeight: 700,
                  boxShadow: active ? '0 0 15px rgba(16, 185, 129, 0.5)' : 'none',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative'
                }}>
                  {/* Glowing Active Ring */}
                  {active && (
                    <div style={{
                      position: 'absolute',
                      top: '-4px',
                      left: '-4px',
                      right: '-4px',
                      bottom: '-4px',
                      border: '2px solid #10b981',
                      borderRadius: '50%',
                      opacity: 0.8,
                      animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite'
                    }} />
                  )}
                  {done && !active ? <FaCheck size={14} style={{ animation: 'scaleIn 0.3s ease' }} /> : i + 1}
                </div>

                {/* Step Metadata */}
                <span style={{
                  fontSize: '0.85rem',
                  marginTop: '8px',
                  color: active ? '#059669' : done ? '#334155' : '#64748b',
                  fontWeight: active ? 700 : 600,
                  transition: 'color 0.3s ease'
                }}>
                  {STEP_LABEL[s]}
                </span>
                <span style={{
                  fontSize: '0.7rem',
                  marginTop: '2px',
                  color: active ? '#10b981' : done ? '#64748b' : '#94a3b8',
                  maxWidth: '120px',
                  lineHeight: '1.2'
                }}>
                  {STEP_DESC[s]}
                </span>
              </div>

              {/* Progress Connector Line */}
              {i < WO_STEPS.length - 1 && (
                <div style={{
                  flex: 1,
                  height: '4px',
                  background: '#e2e8f0',
                  borderRadius: '2px',
                  position: 'relative',
                  minWidth: '40px',
                  alignSelf: 'center',
                  marginBottom: '24px',
                  zIndex: 1,
                  opacity: isCancelled ? 0.3 : 1
                }}>
                  {/* Animated Inner Flow */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: i < idx ? '100%' : '0%',
                    background: 'linear-gradient(90deg, #059669, #10b981)',
                    borderRadius: '2px',
                    boxShadow: i < idx ? '0 0 8px rgba(16, 185, 129, 0.4)' : 'none',
                    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                  }} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
import { workOrderAPI, publisherAdminAPI as publisherAPI, packageAPI, bookAPI, projectAPI } from '../../utils/api';
import DataTable from '../../components_shared/DataTable';
import TableToolbar from '../../components_shared/TableToolbar';
import WorkOrderForm from './WorkOrderForm'; 

export default function WorkOrdersPage() {
  const [rows, setRows] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [books, setBooks] = useState([]);
  const [projects, setProjects] = useState([]);
  
  const [filterPublisher, setFilterPublisher] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [w, v, p, b, pr] = await Promise.all([
        workOrderAPI.getAll(),
        publisherAPI.getAll(),
        packageAPI.getAll(),
        bookAPI.getAll(),
        projectAPI.getAll()
      ]);
      const projArr = Array.isArray(pr.data) ? pr.data : (pr.data?.content || []);
      setRows(Array.isArray(w.data) ? w.data : []);
      setPublishers(Array.isArray(v.data) ? v.data : []);
      setPackages(Array.isArray(p.data) ? p.data : []);
      setBooks(Array.isArray(b.data) ? b.data : []);
      setProjects(projArr);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editRow) await workOrderAPI.update(editRow.id, form);
      else await workOrderAPI.create(form);
      setShowModal(false);
      setEditRow(null);
      await load();
      Swal.fire({ icon: 'success', title: 'Saved successfully', toast: true, position: 'top-end', timer: 3000 });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Save error', text: e.response?.data?.message || e.message });
    }
  };

  const remove = async (id) => {
    const c = await Swal.fire({ title: 'Delete Work Order?', text: 'This will remove the PO record.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' });
    if (c.isConfirmed) {
      try {
        await workOrderAPI.delete(id);
        await load();
        Swal.fire({ title: 'Deleted', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
      } catch (err) { Swal.fire('Error', err.message, 'error'); }
    }
  };

  const exportCsv = async () => {
    try {
      const r = await workOrderAPI.exportCsv();
      const b = new Blob([r.data], { type: 'text/csv' });
      const u = URL.createObjectURL(b);
      const a = document.createElement('a'); a.href = u; a.download = 'work_orders_export.csv'; a.click(); URL.revokeObjectURL(u);
    } catch (e) { Swal.fire('Error', e.message, 'error'); }
  };

  const importCsv = async (csv) => {
    const res = await workOrderAPI.importCsv(csv);
    await load();
    return res.data;
  };

  const downloadSample = async () => {
    try {
      const r = await workOrderAPI.sampleCsv();
      const b = new Blob([r.data], { type: 'text/csv' });
      const u = URL.createObjectURL(b);
      const a = document.createElement('a'); a.href = u; a.download = 'work_orders_sample.csv'; a.click(); URL.revokeObjectURL(u);
    } catch (e) { console.error(e); }
  };

  const nf = new Intl.NumberFormat('en-US');

  const columns = [
    { accessorKey: 'order_number', header: 'PO #' },
    { accessorKey: 'project.name', header: 'Project' },
    { accessorKey: 'publisher.name', header: 'Publisher' },
    { accessorKey: 'order_date', header: 'Date', cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleDateString() : '-' },
    { 
      accessorKey: 'status', 
      header: 'Status',
      cell: ({ getValue }) => (
        <span className={`eduss-global-status-badge ${getValue()?.toLowerCase()}`}>
          {getValue()}
        </span>
      )
    },
    { accessorKey: 'total_quantity', header: 'Qty', cell: ({ getValue }) => nf.format(getValue() || 0) },
    { accessorKey: 'total_price', header: 'Value', cell: ({ getValue }) => `Rs. ${nf.format(getValue() || 0)}` },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => setViewRow(row.original)}><FaFileAlt /></button>
          <button className="eduss-global-action-btn edit" onClick={() => { setEditRow(row.original); setShowModal(true); }}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => remove(row.original.id)}><FaTrash /></button>
        </div>
      ),
    },
  ];

  const filteredData = rows.filter(r => {
    const s = search.toLowerCase();
    const pId = (r.publisher?.id || r.vendor?.id);
    const projId = r.project?.id;
    
    return (!search || r.order_number?.toLowerCase().includes(s) || r.publisher?.name?.toLowerCase().includes(s))
    && (!filterPublisher || String(pId) === filterPublisher)
    && (!filterProject || String(projId) === filterProject)
    && (!filterStatus || r.status === filterStatus);
  });

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Purchase Orders"
        subtitle="Manage work orders and printing contracts for textbooks"
        onAdd={() => { setEditRow(null); setShowModal(true); }}
        onExport={exportCsv}
        onImport={importCsv}
        onDownloadSample={downloadSample}
        onSearch={setSearch}
        onFilterChange={(name, val) => {
          if (name === 'publisher') setFilterPublisher(val);
          if (name === 'project') setFilterProject(val);
          if (name === 'status') setFilterStatus(val);
        }}
        filterOptions={[
          { name: 'publisher', value: filterPublisher, options: publishers.map(v => ({ label: v.name, value: v.id.toString() })) },
          { name: 'project', value: filterProject, options: projects.map(p => ({ label: p.name, value: p.id.toString() })) },
          { name: 'status', value: filterStatus, options: ['DRAFT', 'PENDING', 'APPROVED', 'COMPLETED', 'CANCELLED'].map(s => ({ label: s, value: s })) }
        ]}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>

      {showModal && (
        <div className="eduss-global-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>{editRow ? 'Update Work Order' : 'Create New Work Order'}</h3>
              <button onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <WorkOrderForm 
                initial={editRow} 
                publishers={publishers} 
                packages={packages} 
                books={books} 
                projects={projects} 
                onCancel={() => setShowModal(false)} 
                onSave={save}
              />
            </div>
          </div>
        </div>
      )}

      {viewRow && (
        <div className="eduss-global-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Work Order Details</h3>
              <button onClick={() => setViewRow(null)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <div style={{ display: 'flex', gap: 20, marginBottom: 25, alignItems: 'center' }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(15, 111, 173, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#0f6fad' }}>
                  <FaShoppingBag />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '1.4rem' }}>{viewRow.order_number}</h4>
                  <p style={{ margin: 0, color: '#666' }}>{viewRow.publisher?.name || 'Authorized Publisher'}</p>
                </div>
              </div>
              <StatusTimeline status={viewRow.status} />
              
              {/* Governance Audit Trail Banner */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.75rem',
                color: '#64748b',
                background: 'rgba(15, 111, 173, 0.04)',
                border: '1px dashed rgba(15, 111, 173, 0.2)',
                padding: '10px 14px',
                borderRadius: '8px',
                marginBottom: '20px',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <span style={{ fontSize: '1.1rem' }}>🛡️</span>
                <span>
                  <strong>Governance Audit Log:</strong> Order initialized on {viewRow.created_at ? new Date(viewRow.created_at).toLocaleDateString() : '-'} | Last state mutation verified on {viewRow.updated_at ? new Date(viewRow.updated_at).toLocaleString() : '-'} (Authorized Operator ID: {viewRow.updated_by || 'System'})
                </span>
              </div>
              <table className="mini-info-table">
                <tbody>
                  <tr><th>Project</th><td>{viewRow.project?.name || '-'}</td></tr>
                  <tr><th>Order Date</th><td>{viewRow.order_date ? new Date(viewRow.order_date).toLocaleDateString() : '-'}</td></tr>
                  <tr><th>Package</th><td>{viewRow.package?.name || '-'}</td></tr>
                  <tr><th>Current Status</th><td>{viewRow.status}</td></tr>
                  <tr><th>Total Qty</th><td>{nf.format(viewRow.total_quantity || 0)}</td></tr>
                  <tr><th>Total Value</th><td>Rs. {nf.format(viewRow.total_price || 0)}</td></tr>
                </tbody>
              </table>

              <div style={{ marginTop: 25 }}>
                <h4 style={{ marginBottom: 15 }}>Order Items</h4>
                <div style={{ maxHeight: '300px', overflowY: 'auto', borderRadius: 12, border: '1px solid #f1f1f1' }}>
                  <table className="eduss-global-datatable mini">
                    <thead>
                      <tr>
                        <th>Book Title</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewRow.items?.map((it, idx) => (
                        <tr key={idx}>
                          <td>{it.book_name || it.name || `Book ID: ${it.book_id}`}</td>
                          <td>{nf.format(it.quantity)}</td>
                          <td>Rs. {nf.format(it.unit_price)}</td>
                          <td>Rs. {nf.format(it.quantity * it.unit_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
